import {
  CalendarArrowDown,
  CalendarCheck,
  Calendar as CalendarIcon,
  CalendarRange,
  CheckCircle,
  Flame,
  Hourglass,
  MoveRight,
  Snowflake,
  Undo2,
  UserCheck2,
  Users,
} from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import type { DateData } from "react-native-calendars";
import type { DayProps } from "react-native-calendars/src/calendar/day";
import type { MarkingProps } from "react-native-calendars/src/calendar/day/marking";
import {
  type GetThemeValueForKey,
  ScrollView,
  Square,
  View,
  XStack,
  YStack,
} from "tamagui";
import { TKey } from "../../../shared/translations/translationKeys";
import { useApi } from "../../core/apis";
import { type DoneStep, DoneSteps } from "../../core/components/DoneSteps";
import {
  FormDataModal,
  FormSheetType,
} from "../../core/components/FormDataModal";
import { PrimaryCheck } from "../../core/components/PrimaryCheck";
import {
  areDatesWithinRange,
  formatDate,
  formatDateRange,
  getDayJsDate,
  isSame,
  isSameDay,
} from "../../core/dayJs";
import {
  getDaysInStartEnd,
  groupDateRanges,
  groupDateRangesWithUsers,
  parseLocalDate,
} from "../../core/groupDates";
import { Block, ModuleBlock } from "../../ui/Block";
import { Body } from "../../ui/Body";
import { Box } from "../../ui/Box";
import { Button } from "../../ui/Button";
import { Calendar, CalendarDay } from "../../ui/Calendar";
import { Checkbox } from "../../ui/Checkbox";
import { Expander } from "../../ui/Expander";
import { Group } from "../../ui/Group";
import { InfoRow, type InfoRowItemProps } from "../../ui/InfoRow";
import { Label } from "../../ui/Label";
import { log } from "../../ui/log";
import { Sheet } from "../../ui/Sheet";
import { Subtext } from "../../ui/Subtext";
import { TipsText } from "../../ui/TipsText";
import { Title } from "../../ui/Title";
import { ModuleDataType } from "../core/BaseModuleDataModel";
import type CalendarModuleModel from "./CalendarModuleModel";
import CalendarItem, {
  type CalendarItemModuleModelData,
} from "./data/CalendarItemModel";

const heatMapColors = [
  // Least popular
  { backgroundColor: "#F5FBF7", color: "#222" },
  { backgroundColor: "#D5F0E0", color: "#222" },
  { backgroundColor: "#A6DFBF", color: "#222" },
  { backgroundColor: "#6EC593", color: "#FFF" },
  { backgroundColor: "#3AA76F", color: "#FFF" },
  // Most popular
  { backgroundColor: "#207B4D", color: "#FFF" },
];

function getCalendarDayComponent(module: CalendarModuleModel) {
  const DayComponent: React.FC<
    DayProps & {
      date?: DateData;
    }
  > = observer((props) => {
    const date = props.date;
    if (!date) {
      return null;
    }

    const isSelected =
      module.isDone && module.data.selectedDates?.includes(date.dateString);

    const user = module.plan.userInPlan;

    const userSelects = module.dataItems.reduce<string[]>((acc, item) => {
      if (CalendarItem.is(item)) {
        if (item.data.selectedDates.includes(date.dateString)) {
          acc.push(item.data.userId);
        }
      }

      return acc;
    }, []);

    if (user && props.marking?.selected && !userSelects.includes(user.uid)) {
      userSelects.push(user.uid);
    }

    const totalUsers = module.plan.data.userIds.length;
    const ratio = totalUsers === 0 ? 0 : userSelects.length / totalUsers;
    const rawIndex = Math.floor(ratio * 5);
    const heatMapIndex = Math.min(rawIndex, heatMapColors.length - 1);

    let colors: { backgroundColor?: string; color?: string } = {};

    if (module.isDone) {
      colors = isSelected
        ? { backgroundColor: "$primary", color: undefined }
        : {};
    } else if (userSelects.length) {
      colors = heatMapColors[heatMapIndex];
    }

    const { backgroundColor, color } = colors;

    return (
      <CalendarDay
        userSelects={module.isDone ? undefined : userSelects}
        {...props}
        marking={module.isDone && !isSelected ? undefined : props.marking}
        backgroundColor={backgroundColor}
        color={color}
      />
    );
  });

  return DayComponent;
}

type SelectedDatesMap = {
  [key: string]: {
    selected?: boolean;
  };
};

const CalendarDoneStops: React.FC<{ module: CalendarModuleModel }> = observer(
  ({ module }) => {
    const doneSteps = useMemo<DoneStep[]>(() => {
      return [
        {
          id: "select-dates",
          icon: CalendarCheck,
          title: "Select dates",
          description:
            "Select the dates in the calendar where you are available",
        },
        {
          skip: module.plan.usersCount > 1,
          id: "invite-users",
          icon: CalendarCheck,
          title: "Invite people",
          description:
            "Invite people so they can pick the dates where they're available",
          onPress: () => module.plan.setShowShare(true, true),
        },
        {
          id: "wait",
          icon: Hourglass,
          title: "Wait for everyone to select dates",
          description: `We're still waiting for ${module.waitingForUsers.join(", ")} to answer`,
          noAction: true,
          onPress: () => module.onDone(),
        },
        {
          id: "done",
          icon: CheckCircle,
          title: "Decide final dates",
          description:
            "Everyone has voted and it's time to select the final dates",
          onPress: () => module.onDone(),
        },
      ];
    }, [module, module.waitingForUsers]);

    let activeDoneStepId: string;
    if (!module.hasAddedDates) {
      activeDoneStepId = "select-dates";
    } else if (module.plan.usersCount === 1) {
      activeDoneStepId = "invite-users";
    } else if (module.waitingForUsers.length > 0) {
      activeDoneStepId = "wait";
    } else {
      activeDoneStepId = "done";
    }

    return (
      <DoneSteps
        module={module}
        steps={doneSteps}
        activeId={activeDoneStepId}
      />
    );
  },
);

const SelectedRangesItem: React.FC<{
  range: string;
  start: Date;
  end: Date;
  users: string[];
  active?: boolean;
  module: CalendarModuleModel;
  withCheckbox?: boolean;
  onDatePress?: (date: string) => void;
}> = observer(
  ({ range, users, module, active, withCheckbox, onDatePress, start, end }) => {
    const usersInPlan = module.plan.data.userIds.length;
    const isSelected =
      module.isDone &&
      areDatesWithinRange(module.data.selectedDates ?? [], start, end);

    const heatMapIndex = Math.min(
      Math.floor(((users.length / usersInPlan) * 100) / 20),
      heatMapColors.length - 1,
    );

    const sameDay = isSameDay(start, end);
    const sameMonth = isSame(start, end, "month");
    const sameYear =
      isSame(start, end, "year") && isSame(new Date(), start, "year");
    const startText = formatDate(start, sameYear, !sameDay && sameMonth);
    const endText = sameDay ? undefined : formatDate(end, sameYear);

    const { backgroundColor, color } = heatMapColors[heatMapIndex];

    const infoRowItems = useMemo<InfoRowItemProps[]>(() => {
      const isEveryone = users.length >= usersInPlan;
      const daysInRange = getDaysInStartEnd(start, end);
      const daysInRageSuffix = daysInRange === 1 ? "day" : "days";
      return [
        {
          icon: CalendarRange,
          text: `${getDaysInStartEnd(start, end)} ${daysInRageSuffix}`,
        },
        {
          icon: UserCheck2,
          text: isEveryone ? "Everyone" : `${Array.from(users).join(", ")}`,
          trim: ", ",
        },
      ];
    }, [users, start, end, usersInPlan]);

    return (
      <Block
        onPress={onDatePress && (() => onDatePress?.(range))}
        icon={withCheckbox ? <Checkbox checked={!!active} /> : undefined}
        grouped
        opacity={module.isDone && !isSelected ? 0.3 : 1}
      >
        <XStack width="100%" gap="$medium">
          <Square size={34} position="relative" style={{ backgroundColor }}>
            <Body style={{ color }}>{users.length}</Body>
            {isSelected && (
              <View position="absolute">
                <PrimaryCheck bottom={-15} right={-15} />
              </View>
            )}
          </Square>
          <YStack flex={1} gap="$small">
            <XStack alignItems="center" gap="$medium" width="100%">
              <Label>{startText}</Label>
              {endText && (
                <>
                  <MoveRight opacity={0.4} />
                  <Label>{endText}</Label>
                </>
              )}
            </XStack>
            <InfoRow items={infoRowItems} />
          </YStack>
        </XStack>
      </Block>
    );
  },
);

const SortDatesSelector: React.FC<{
  sortBy: string;
  setSortBy: React.Dispatch<React.SetStateAction<"POPULARITY" | "DATE">>;
}> = observer((props) => {
  return (
    <XStack
      alignItems="center"
      gap="$small"
      cursor="pointer"
      onPress={() =>
        props.setSortBy((current) => {
          if (current === "DATE") {
            return "POPULARITY";
          }

          return "DATE";
        })
      }
    >
      <Body>Sort by</Body>
      {props.sortBy === "DATE" ? <Flame /> : <CalendarArrowDown />}
    </XStack>
  );
});

const GroupDates: React.FC<{
  onChange?: () => void;
  setGroupRanges: React.Dispatch<React.SetStateAction<boolean>>;
  groupRanges: boolean;
}> = observer(({ onChange, setGroupRanges, groupRanges }) => {
  return (
    <XStack
      gap="$medium"
      alignItems="center"
      cursor="pointer"
      marginLeft={4}
      onPress={(e) => {
        e.stopPropagation();
        setGroupRanges((checked) => !checked);
        onChange?.();
      }}
    >
      <Checkbox checked={groupRanges} />
      <Body>Group ranges</Body>
    </XStack>
  );
});

const SelectedDates: React.FC<{
  module: CalendarModuleModel;
  onDatePress?: (date: string) => void;
  activeDates?: string[];
  groupRanges?: boolean;
  onGroupChange?: () => void;
}> = observer(({ module, onDatePress, activeDates, onGroupChange }) => {
  const [sortBy, setSortBy] = useState<"POPULARITY" | "DATE">("POPULARITY");
  const [groupRanges, setGroupRanges] = useState<boolean>(
    module.data.settings.groupDays,
  );
  const groupedDates = groupRanges
    ? groupDateRangesWithUsers(
        module.calendarItems.map(([date, users]) => ({ date, users })),
      )
    : module.calendarItems.map(([date, users]) => ({
        range: date,
        users,
        start: parseLocalDate(date),
        end: parseLocalDate(date),
      }));

  return (
    <YStack gap="$large">
      <XStack justifyContent="space-between" alignItems="center">
        <GroupDates
          groupRanges={groupRanges}
          setGroupRanges={setGroupRanges}
          onChange={onGroupChange}
        />
        <SortDatesSelector sortBy={sortBy} setSortBy={setSortBy} />
      </XStack>
      <Group>
        {groupedDates
          .toSorted((a, b) => {
            const selectedDates = module.isDone
              ? (module.data.selectedDates ?? [])
              : [];
            const isASelected = areDatesWithinRange(
              selectedDates,
              a.start,
              a.end,
            );
            const isBSelected = areDatesWithinRange(
              selectedDates,
              b.start,
              b.end,
            );

            if (isASelected !== isBSelected) {
              return isASelected ? -1 : 1;
            }

            if (sortBy === "DATE") {
              return a.start > b.start ? 1 : -1;
            }

            const aUserSelects = a.users.length;
            const bUserSelects = b.users.length;

            if (aUserSelects !== bUserSelects) {
              return aUserSelects > bUserSelects ? -1 : 1;
            }

            return a.start > b.start ? 1 : -1;
          })
          .map(({ range, users, start, end }) => (
            <SelectedRangesItem
              key={range}
              start={start}
              end={end}
              range={range}
              users={Array.from(users)}
              module={module}
              withCheckbox={!!activeDates}
              active={activeDates?.includes(range)}
              onDatePress={onDatePress}
            />
          ))}
      </Group>
    </YStack>
  );
});

const CalendarDoneSheet: React.FC<{ module: CalendarModuleModel }> = observer(
  ({ module }) => {
    const [selectedRanges, setSelectedDates] = useState<string[]>([]);

    const handleDateSelect = useCallback(
      (newDate: string) => {
        if (selectedRanges.includes(newDate)) {
          setSelectedDates((dates) => dates.filter((date) => date !== newDate));
        } else {
          setSelectedDates((dates) => dates.concat([newDate]));
        }
      },
      [selectedRanges],
    );

    return (
      <Sheet
        sheetId="calendar_done"
        open={module.doneFlowEnabled}
        onOpenChange={module.disableDoneFlow.bind(module)}
        header={
          <YStack gap="$space.small">
            <Title>Decide final dates</Title>
            <Body>
              {selectedRanges.length
                ? `${selectedRanges.length} days selected`
                : "Select the dates you've decided on to mark the card as completed"}
            </Body>
          </YStack>
        }
        footer={
          <YStack gap="$medium">
            <TipsText text="When saved you will see the final dates in your Planira" />
            <Button
              onPress={() => module.setDone(selectedRanges)}
              disabled={!selectedRanges.length}
              icon={CheckCircle}
            >
              Save dates
            </Button>
          </YStack>
        }
      >
        <ScrollView>
          <SelectedDates
            activeDates={selectedRanges}
            module={module}
            onDatePress={handleDateSelect}
            onGroupChange={() => setSelectedDates([])}
          />
        </ScrollView>
      </Sheet>
    );
  },
);

const HeatMap: React.FC = () => {
  return (
    <YStack gap="$small">
      <Subtext>Popularity / Heat map</Subtext>
      <XStack>
        {heatMapColors.map((color, i) => {
          const isFirst = i === 0;
          let icon: JSX.Element | undefined;
          if (isFirst) {
            icon = (
              <Snowflake color={color.color as GetThemeValueForKey<"color">} />
            );
          } else if (i === heatMapColors.length - 1) {
            icon = (
              <Flame color={color.color as GetThemeValueForKey<"color">} />
            );
          }
          return (
            <View
              padding={"$small"}
              key={color.backgroundColor}
              style={{
                backgroundColor: isFirst ? undefined : color.backgroundColor,
              }}
              borderWidth={isFirst ? 1 : 0}
              borderColor={"$divider"}
              width={`${100 / heatMapColors.length}%`}
              alignItems="center"
            >
              {icon}
            </View>
          );
        })}
      </XStack>
    </YStack>
  );
};

export const CalendarModuleStaticUI: React.FC<{ module: CalendarModuleModel }> =
  observer(({ module }) => {
    const isDone = module.isDone;
    const [dates, setDates] = useState(
      () => new Set<string>(module.data.selectedDates),
    );

    const setDate = useCallback(
      (data: DateData) => {
        if (!isDone) {
          setDates((prevDates) => {
            const newDates = new Set(prevDates);

            if (newDates.has(data.dateString)) {
              newDates.delete(data.dateString);
            } else {
              newDates.add(data.dateString);
            }

            return newDates;
          });
        }
      },
      [isDone],
    );

    const markedDates = useMemo(() => {
      const d: { [key: string]: MarkingProps } = {};

      dates.forEach((date) => {
        d[date] = {
          color: "$primary",
        };
      });

      return d;
    }, [dates]);

    const seleectedDates = Array.from(dates.values());

    return (
      <>
        <Box>
          <Calendar
            markedDates={markedDates}
            startOfWeek={module.plan.data.settings.startOfWeek}
            onDayPress={setDate}
          />
        </Box>
        <View>
          {seleectedDates.length ? (
            <Box>
              <Body>
                You have selected the following dates{" "}
                {groupDateRanges(seleectedDates)
                  .map((range) => formatDateRange(range, true))
                  .join(", ")}
              </Body>
            </Box>
          ) : undefined}
          {isDone ? (
            <Button
              type="secondary"
              onPress={module.toggleDoneState.bind(module)}
              icon={Undo2}
            >
              Revert done status
            </Button>
          ) : (
            <Button
              disabled={!dates.size}
              onPress={() => module.setDone(Array.from(dates.values()))}
              icon={CheckCircle}
            >
              Save dates
            </Button>
          )}
        </View>
      </>
    );
  });

export const CalendarModuleUI: React.FC<{ module: CalendarModuleModel }> =
  observer(({ module }) => {
    const { modulesDataApi } = useApi();

    const user = module.plan.userInPlan;
    const userCalendarItem = module.userCalendarItem;

    const [selectedDates, setSelectedDates] = useState<
      SelectedDatesMap | undefined
    >();
    const [currentDate, setCurrentDate] = useState(
      () => module.calendarItems[0]?.[0],
    );

    const goToMonth = useCallback((date: string) => {
      const startDate = date.split(" - ")[0]?.trim();
      // Hack it so that the date is actually changed and a re-render is triggered
      setCurrentDate((prevDate) => {
        if (prevDate === startDate) {
          setTimeout(() => {
            setCurrentDate(startDate);
          }, 1);

          return "";
        }

        return startDate;
      });
    }, []);

    useEffect(() => {
      if (!currentDate) {
        setCurrentDate(module.calendarItems[0]?.[0]);
      }
    }, [currentDate, module.calendarItems[0]]);

    useEffect(() => {
      if (!selectedDates && userCalendarItem) {
        setSelectedDates(
          userCalendarItem.data.selectedDates.reduce<SelectedDatesMap>(
            (acc, date) => {
              acc[date] = { selected: true };
              return acc;
            },
            {},
          ),
        );
      }
    }, [userCalendarItem, selectedDates]);

    useEffect(() => {
      const unsub = modulesDataApi.watchDataForModuleUid(module.uid);
      return () => {
        log.network(
          `Stopped watchDataForModuleUid from moduleUid ${module.uid}`,
        );
        unsub();
      };
    }, [modulesDataApi, module.uid]);

    const addCalendarDate = useCallback(
      (datesMap: SelectedDatesMap) => {
        if (!module.isEditable) {
          return;
        }

        const dates = Object.keys(datesMap);

        if (userCalendarItem) {
          userCalendarItem.setSelectedDates(dates);
        } else if (user && dates.length) {
          module.addNewData<CalendarItemModuleModelData>(
            ModuleDataType.CalendarItem,
            {
              userId: user.uid,
              selectedDates: dates,
            },
            TKey.ActivityCalendarDatesSet,
            {
              dates: groupDateRanges(dates.slice())
                .map((range) => formatDateRange(range, true))
                .join(", "),
            },
          );
        }
      },
      [userCalendarItem, user, module],
    );

    const CalendarDayComponent = useMemo(() => {
      return getCalendarDayComponent(module);
    }, [module]);

    return (
      <YStack gap="$medium">
        <Box>
          <Calendar
            startOfWeek={module.plan.data.settings.startOfWeek}
            minDate={
              module.data.settings.minDate?.toDate().toDateString() ??
              new Date().toISOString()
            }
            maxDate={
              module.data.settings.maxDate?.toDate().toDateString() ?? undefined
            }
            initialDate={currentDate}
            markedDates={selectedDates}
            onDayPress={(day) => {
              if (!module.isEditable) {
                return;
              }

              setSelectedDates((dates) => {
                const newDates = { ...dates };

                if (newDates[day.dateString]) {
                  delete newDates[day.dateString];
                } else {
                  newDates[day.dateString] = { selected: true };
                }

                addCalendarDate(newDates);

                return newDates;
              });
            }}
            dayComponent={CalendarDayComponent}
          />
          {!module.isDone && <HeatMap />}
        </Box>
        {module.calendarItems?.length ? (
          <Box>
            <Expander maxHeight={500}>
              <SelectedDates
                onDatePress={goToMonth}
                module={module}
                groupRanges={module.data.settings.groupDays}
              />
            </Expander>
          </Box>
        ) : undefined}

        <CalendarDoneStops module={module} />
        <CalendarDoneSheet module={module} />

        <FormDataModal
          visible={module.showSettingsFormModal}
          onClose={() => {
            module.toggleSettingsFormModal();
          }}
          title="Setup calendar min and/or max date"
          values={useMemo(
            () => ({
              minDate:
                getDayJsDate(
                  module.data.settings.minDate,
                  module.plan.data.settings.dateFormat,
                ) ?? "",
              maxDate:
                getDayJsDate(
                  module.data.settings.maxDate,
                  module.plan.data.settings.dateFormat,
                ) ?? "",
            }),
            [
              module.data.settings.minDate,
              module.data.settings.maxDate,
              module.plan.data.settings.dateFormat,
            ],
          )}
          form={[
            {
              formKey: "minDate",
              label: "First available date",
              type: FormSheetType.Date,
              icon: CalendarIcon,
              placeholder: "First available date",
              plan: module.plan, // for settings
            },
            {
              formKey: "maxDate",
              label: "Last available date",
              type: FormSheetType.Date,
              icon: CalendarIcon,
              placeholder: "Last available date",
              plan: module.plan, // for settings
            },
          ]}
          onSave={(data) => {
            module.setSettingsFormData(`${data.minDate}`, `${data.maxDate}`);
          }}
        />
      </YStack>
    );
  });

export const CalendarModuleBlockUI: React.FC<{ module: CalendarModuleModel }> =
  observer(({ module }) => {
    const infoRowItems = React.useMemo<InfoRowItemProps[]>(
      () => [
        {
          text: `${module.data.snapshot.numParticipants ?? 0} of ${
            module.plan.data.userIds.length
          }`,
          icon: Users,
        },
      ],
      [module.data.snapshot, module.plan.data.userIds],
    );

    return (
      <ModuleBlock
        module={module}
        infoRowItems={infoRowItems}
        doneText={
          module.data.selectedDates
            ? groupDateRanges(module.data.selectedDates.slice())
                .map((range) => formatDateRange(range, true))
                .join(", ")
            : undefined
        }
      />
    );
  });

export const CalendarStaticModuleBlockUI: React.FC<{
  module: CalendarModuleModel;
}> = observer(({ module }) => {
  const infoRowItems = React.useMemo<InfoRowItemProps[]>(
    () => [
      {
        text: "Tap to select dates",
        icon: CalendarCheck,
      },
    ],
    [],
  );

  return (
    <ModuleBlock
      module={module}
      infoRowItems={!module.isDone ? infoRowItems : []}
      doneText={
        module.data.selectedDates
          ? groupDateRanges(module.data.selectedDates.slice())
              .map((range) => formatDateRange(range, true))
              .join(", ")
          : undefined
      }
    />
  );
});
